import { useCallback, useEffect, useId, useState } from 'react';
import { PALJA_CATALOG_ADMIN_TEST_IDS as IDS } from '@edusense/shared-constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  catalogDraft, getPaljaCatalog, paljaCatalogError, savePaljaCatalogProduct, uploadPaljaCover,
  validatePaljaCatalogDraft, validatePaljaCover, type PaljaCatalogProduct,
} from './paljaCatalogApi';

export function PaljaBookCoverPreview({ product, selectedUrl }: { product: PaljaCatalogProduct; selectedUrl?: string }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const url = selectedUrl === undefined ? product.coverUrl : selectedUrl;
  const lightCover = product.coverStyle === 'cream';
  const redCover = product.coverStyle === 'red' || product.coverStyle === 'consultation';
  return (
    <div className="relative mx-auto aspect-[2/3] w-full max-w-40 overflow-hidden rounded-sm border border-black/20 shadow-md"
      style={{ background: lightCover ? '#f0eadb' : redCover ? '#b8191f' : '#20211e', color: lightCover ? '#24251f' : '#fff8e9' }}>
      {failedUrl === url ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-2 text-center text-xs">
          <span>표지를 불러오지 못했습니다.</span>
          <Button size="sm" variant="outline" onClick={() => { setFailedUrl(null); setAttempt(value => value + 1); }}>다시 보기</Button>
        </div>
      ) : (
        <>
          <img key={`${url}-${attempt}`} src={url} alt={`${product.title} 표지 그림`} className="absolute inset-0 h-full w-full object-fill" onError={() => setFailedUrl(url)} />
          <div className="absolute inset-x-3 top-4 whitespace-pre-line break-keep text-center text-base font-black leading-tight">{product.title}</div>
          <div className="absolute inset-x-3 bottom-3 border-t border-current/30 pt-2 text-[9px]">팔자폭격</div>
        </>
      )}
    </div>
  );
}

function CatalogProductEditor({ product, token, onChanged, onReload }: {
  product: PaljaCatalogProduct;
  token: string;
  onChanged: (product: PaljaCatalogProduct) => void;
  onReload: () => Promise<void>;
}) {
  const prefix = useId();
  const [draft, setDraft] = useState(() => catalogDraft(product));
  const [revision, setRevision] = useState(product.revision);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [busy, setBusy] = useState<'metadata' | 'cover' | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const newer = product.revision !== revision;
  const blocked = busy !== null || conflict || newer;
  const dirty = draft.title !== product.title || draft.description !== product.description || draft.active !== product.active || draft.sortOrder !== product.sortOrder;

  useEffect(() => {
    if (file === null) { setPreviewUrl(undefined); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const applyLatest = (keepDraft: boolean) => {
    if (!keepDraft) setDraft(catalogDraft(product));
    setRevision(product.revision);
    setConflict(false);
    setError('');
    setMessage(keepDraft ? '수정 중인 내용을 유지했습니다. 확인 후 저장하세요.' : '최신 정보를 불러왔습니다.');
  };

  const perform = async (kind: 'metadata' | 'cover') => {
    if (blocked || (kind === 'cover' && file === null)) return;
    if (kind === 'metadata' && (!dirty || validatePaljaCatalogDraft(draft) !== null)) return;
    setError(''); setMessage(''); setBusy(kind);
    try {
      const updated = kind === 'metadata'
        ? await savePaljaCatalogProduct(token, product.id, draft, revision)
        : await uploadPaljaCover(token, product.id, file as File, revision);
      onChanged(updated);
      setRevision(updated.revision);
      if (kind === 'metadata') setDraft(catalogDraft(updated));
      else setFile(null);
      setMessage(kind === 'metadata' ? '책 정보를 저장했습니다.' : '표지를 적용했습니다. 앱에서 새 표지를 가져옵니다.');
    } catch (cause) {
      const failure = paljaCatalogError(cause);
      setError(failure.message);
      if (failure.status === 409) {
        setConflict(true);
        await onReload();
      }
    } finally { setBusy(null); }
  };

  const selectFile = (selected: File | undefined) => {
    if (!selected) return;
    const validation = validatePaljaCover(selected);
    setMessage('');
    if (validation) { setFile(null); setError(validation); return; }
    setError(''); setFile(selected);
  };

  return (
    <article data-testid={IDS.product(product.id)} className="min-w-0 rounded-xl border bg-card p-4 shadow-sm">
      <div className="mb-5 flex items-start gap-4">
        <div className="w-28 shrink-0">
          <PaljaBookCoverPreview product={{ ...product, title: draft.title }} selectedUrl={previewUrl} />
        </div>
        <div className="min-w-0 space-y-2">
          <h3 className="break-keep text-base font-bold">{product.title}</h3>
          <p className="text-sm text-muted-foreground">{product.pageCount}쪽 · {product.priceWon.toLocaleString('ko-KR')}원<br />미리보기 {product.previewPageCount}쪽</p>
          <p className={`w-fit rounded-full px-2 py-1 text-xs font-semibold ${product.active ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{product.active ? '책방에 표시' : '판매 숨김'}</p>
          {!product.available && product.unavailableReason && <p className="text-xs text-amber-800">{product.unavailableReason}</p>}
        </div>
      </div>

      <form className="space-y-3" onSubmit={event => { event.preventDefault(); void perform('metadata'); }}>
        <label className="block space-y-1 text-sm" htmlFor={`${prefix}-title`}>
          <span className="font-semibold">책 제목</span>
          <Input id={`${prefix}-title`} data-testid={IDS.title(product.id)} value={draft.title} maxLength={100} required disabled={busy !== null} onChange={event => setDraft({ ...draft, title: event.target.value })} />
        </label>
        <label className="block space-y-1 text-sm" htmlFor={`${prefix}-description`}>
          <span className="font-semibold">책 소개</span>
          <textarea id={`${prefix}-description`} data-testid={IDS.description(product.id)} value={draft.description} required maxLength={1000} rows={3} disabled={busy !== null} className="w-full resize-y rounded-md border border-input bg-background p-2" onChange={event => setDraft({ ...draft, description: event.target.value })} />
        </label>
        <div className="flex flex-wrap items-end gap-4">
          <label className="block w-32 space-y-1 text-sm" htmlFor={`${prefix}-sort`}>
            <span className="font-semibold">정렬 순서</span>
            <Input id={`${prefix}-sort`} data-testid={IDS.sortOrder(product.id)} type="number" min={0} max={9999} step={1} value={Number.isNaN(draft.sortOrder) ? '' : draft.sortOrder} required disabled={busy !== null} onChange={event => setDraft({ ...draft, sortOrder: event.target.value === '' ? NaN : Number(event.target.value) })} />
          </label>
          <label className="flex min-h-10 items-center gap-2 text-sm"><input data-testid={IDS.active(product.id)} type="checkbox" checked={draft.active} disabled={busy !== null} onChange={event => setDraft({ ...draft, active: event.target.checked })} />책방에 표시</label>
        </div>
        <Button type="submit" data-testid={IDS.save(product.id)} disabled={blocked || !dirty || validatePaljaCatalogDraft(draft) !== null}>{busy === 'metadata' ? '저장 중…' : '책 정보 저장'}</Button>
      </form>

      <div className="mt-5 space-y-2 border-t pt-4">
        <label htmlFor={`${prefix}-file`} className="block text-sm font-semibold">표지 그림 교체</label>
        <input id={`${prefix}-file`} data-testid={IDS.coverFile(product.id)} type="file" accept="image/png,image/jpeg,image/webp" disabled={busy !== null} className="block w-full text-sm file:mr-2 file:rounded-md file:border file:bg-background file:px-3 file:py-2" onChange={event => { selectFile(event.target.files?.[0]); event.target.value = ''; }} />
        <p className="text-xs text-muted-foreground">PNG·JPG·WebP, 최대 8MB. 가로·세로 256~4,096px, 총 1,200만 화소 이하.<br />세로형 2:3을 권장합니다. 제목은 앱에서 표시하므로 그림만 올려주세요.</p>
        {file && <div className="flex flex-wrap items-center gap-2"><span className="break-all text-xs">{file.name} · 아직 적용 전</span><Button variant="outline" size="sm" disabled={busy !== null} onClick={() => setFile(null)}>선택 취소</Button></div>}
        <Button data-testid={IDS.upload(product.id)} variant="outline" disabled={blocked || file === null} onClick={() => void perform('cover')}>{busy === 'cover' ? '업로드 중…' : '선택한 표지 적용'}</Button>
      </div>

      {(conflict || newer) && <div className="mt-4 space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
        <p className="font-semibold">서버에 더 최신 정보가 있습니다. 작성 중인 내용은 유지했습니다.</p>
        {newer ? <>
          <p className="break-words">최신 제목: {product.title}<br />최신 설명: {product.description}<br />{product.active ? '책방에 표시' : '판매 숨김'} · 순서 {product.sortOrder}</p>
          <div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" onClick={() => applyLatest(false)}>최신 값으로 다시 편집</Button><Button size="sm" variant="outline" onClick={() => applyLatest(true)}>내 수정 유지하고 계속</Button></div>
        </> : <Button size="sm" variant="outline" onClick={() => void onReload()}>최신 정보 다시 조회</Button>}
      </div>}
      {error && <p role="alert" className="mt-3 break-words rounded-md bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      {message && <p role="status" className="mt-3 rounded-md bg-green-50 p-2 text-sm text-green-800">{message}</p>}
      <p className="mt-4 break-all text-[11px] text-muted-foreground">{product.id} · 수정 버전 {product.revision}<br />{product.updatedAt}</p>
    </article>
  );
}

export default function PaljaBookCatalogPanel({ token }: { token: string | null | undefined }) {
  const [products, setProducts] = useState<PaljaCatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async (signal?: AbortSignal) => {
    if (!token) { setError('관리자 계정으로 로그인하세요.'); setLoading(false); return; }
    setLoading(true); setError('');
    try {
      const catalog = await getPaljaCatalog(token, signal);
      if (!signal?.aborted) setProducts(catalog);
    } catch (cause) {
      if (!signal?.aborted) setError(paljaCatalogError(cause).message);
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [token]);
  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);

  const updateProduct = (product: PaljaCatalogProduct) => setProducts(current => current.map(item => item.id === product.id ? product : item).sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)));
  return (
    <section data-testid={IDS.panel} className="space-y-4" aria-labelledby="palja-catalog-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 id="palja-catalog-heading" className="text-xl font-bold">팔자폭격 책방</h2><p className="mt-1 max-w-2xl text-sm text-muted-foreground">제목·소개·표지·표시 여부·정렬 순서를 관리합니다. 저장한 정보는 앱에서 책방을 다시 열면 반영됩니다. 기존 구매자의 책 내용은 유지됩니다.</p></div>
        <Button data-testid={IDS.refresh} variant="outline" disabled={loading} onClick={() => void load()}>{loading ? '불러오는 중…' : '최신 목록 조회'}</Button>
      </div>
      <p className="rounded-lg border bg-muted/30 p-3 text-sm">가격은 4,000원, 분량은 9쪽, 미리보기는 2쪽입니다. 판매를 숨겨도 이미 구매한 책은 내 책장에서 읽을 수 있습니다.</p>
      {error && <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error} <Button size="sm" variant="outline" disabled={loading} onClick={() => void load()}>다시 불러오기</Button></div>}
      {loading && products.length === 0 && <p role="status" className="p-8 text-center text-muted-foreground">책 목록을 불러오고 있습니다.</p>}
      {!loading && !error && products.length === 0 && <p className="p-8 text-center text-muted-foreground">등록된 책이 없습니다.</p>}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-3">
        {products.map(product => <CatalogProductEditor key={product.id} product={product} token={token ?? ''} onChanged={updateProduct} onReload={() => load()} />)}
      </div>
    </section>
  );
}
